import React from "react";
import { Link } from "react-router-dom";
import {
  Sun, Zap, Leaf, Truck, Factory, Trees, Waves,
  ArrowRight, TrendingUp, ShieldCheck, Award, Users, Building2, MapPin, Play,
  FileCheck, BarChart3, Wrench, Sparkles
} from "lucide-react";

const IMG = {
  solarField:   "https://images.unsplash.com/photo-1509391366360-2e959784a276",
  solarCard:    "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d",
  windSunset:   "https://images.unsplash.com/photo-1466629437334-b4f6603563c5",
  wheatField:   "https://images.unsplash.com/photo-1635174815612-fd9636f70146",
  farmers:      "https://images.unsplash.com/photo-1500937386664-56d1dfef3854",
  engineerSite: "https://images.unsplash.com/photo-1694521787162-5373b598945c",
  inspector:    "https://images.unsplash.com/photo-1716037991590-c975184b37df",
  evCharger:    "https://images.unsplash.com/photo-1593941707882-a5bba14938c7",
  corporate:    "https://images.unsplash.com/photo-1758518727613-00192aed759b",
};

const KPIS = [
  { label: "Solar Capacity Installed", value: "1,240 MW", delta: "+18%", icon: Sun },
  { label: "Villages Electrified (Biogas)", value: "3,540", delta: "+412", icon: Leaf },
  { label: "EV Charging Stations", value: "687", delta: "+126", icon: Zap },
  { label: "Beneficiary Households", value: "4.2 Lakh", delta: "+22%", icon: Users },
];

const SERVICES = [
  { key: "solar_rooftop", title: "Solar Rooftop", desc: "Domestic & commercial rooftop subsidies", icon: Sun, tone: "from-amber-400 to-orange-500" },
  { key: "street_light",  title: "Street Lighting", desc: "LED conversion & smart lighting", icon: Zap, tone: "from-yellow-400 to-amber-500" },
  { key: "biogas",        title: "Biogas Plants",   desc: "Rural biogas cluster programme", icon: Leaf, tone: "from-emerald-500 to-teal-600" },
  { key: "cbg",           title: "Compressed Biogas", desc: "Industrial-scale CBG plants",  icon: Factory, tone: "from-green-500 to-emerald-600" },
  { key: "waste_to_energy", title: "Waste-to-Energy", desc: "Urban & industrial waste plants", icon: Waves, tone: "from-cyan-500 to-teal-600" },
  { key: "ev",            title: "EV Infrastructure", desc: "Charging network & EV incentives", icon: Truck, tone: "from-blue-500 to-indigo-600" },
  { key: "net_metering",  title: "Net Metering", desc: "Rooftop grid connection & metering", icon: BarChart3, tone: "from-lime-500 to-green-600" },
  { key: "subsidy",       title: "Subsidy Applications", desc: "Track and apply for subsidies", icon: FileCheck, tone: "from-fuchsia-500 to-rose-600" },
];

const NEWS = [
  { tag: "Announcement", date: "12 Jan 2026", title: "PEDA launches Solar Rooftop Phase-IV with enhanced subsidy" },
  { tag: "Tender", date: "10 Jan 2026", title: "EPC tender for 50 MW utility solar park in Bathinda floated" },
  { tag: "Success", date: "05 Jan 2026", title: "Sangrur CBG plant crosses 12 TPD production milestone" },
  { tag: "Policy", date: "02 Jan 2026", title: "Revised Net Metering guidelines effective FY2026" },
];

const STATS = [
  { n: "22", l: "Districts Covered" },
  { n: "₹1,850 Cr", l: "Budget Allocation" },
  { n: "150+", l: "Empanelled Vendors" },
  { n: "12", l: "Years of Legacy" },
];

export default function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden hero-punjab text-white">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="container-x py-20 lg:py-28 relative grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--accent))]"/> Government of Punjab · Digital Transformation Portal
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-[1.05]">
              Powering Punjab's <span className="text-[hsl(var(--primary))]">Renewable</span> Future
            </h1>
            <p className="mt-5 max-w-xl text-white/80 text-lg">
              Apply for solar, biogas, CBG, EV and subsidy schemes. Track projects across all 22 districts. Trusted, transparent and citizen-first.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/services" className="btn-primary text-base"><Sun className="h-4 w-4"/>Explore Citizen Services</Link>
              <Link to="/tenders" className="btn-outline bg-white/5 text-white border-white/20 hover:bg-white/10">
                <FileCheck className="h-4 w-4"/> View Active Tenders
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-4 gap-4">
              {STATS.map((s) => (
                <div key={s.l}>
                  <div className="font-heading text-2xl lg:text-3xl font-bold text-[hsl(var(--accent))]">{s.n}</div>
                  <div className="text-[11px] uppercase tracking-widest text-white/60 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Bento asymmetric cards */}
          <div className="relative grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
            <div className="col-span-4 row-span-4 rounded-2xl overflow-hidden ring-1 ring-white/15 relative">
              <img src={IMG.solarField} alt="Solar field" className="h-full w-full object-cover"/>
              <div className="absolute bottom-3 left-3 right-3 glass-dark rounded-xl p-3">
                <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--accent))]">Utility Solar Park</div>
                <div className="font-heading font-semibold">Bathinda — 50 MW Commissioning</div>
              </div>
            </div>
            <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden ring-1 ring-white/15 relative">
              <img src={IMG.evCharger} alt="EV charger" className="h-full w-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"/>
              <div className="absolute bottom-3 left-3 text-sm font-medium">EV Network · 687 stations</div>
            </div>
            <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden ring-1 ring-white/15 relative">
              <img src={IMG.wheatField} alt="Punjab fields" className="h-full w-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"/>
              <div className="absolute bottom-3 left-3 text-sm font-medium">Biogas · Sangrur cluster</div>
            </div>
            <div className="col-span-3 row-span-2 rounded-2xl glass-dark p-4 flex flex-col justify-between">
              <div className="label-tiny text-[hsl(var(--primary))]">Live Grid Health</div>
              <div>
                <div className="text-3xl font-heading font-bold">98.6%</div>
                <div className="text-xs text-white/60">Uptime across renewable sources</div>
              </div>
            </div>
            <div className="col-span-3 row-span-2 rounded-2xl glass-dark p-4 flex flex-col justify-between">
              <div className="label-tiny text-[hsl(var(--accent))]">Applications Today</div>
              <div>
                <div className="text-3xl font-heading font-bold">1,284</div>
                <div className="text-xs text-white/60">Solar · EV · Net Metering</div>
              </div>
            </div>
          </div>
        </div>
        {/* announcement ticker */}
        <div className="border-t border-white/10 bg-black/30 overflow-hidden">
          <div className="container-x py-2 flex items-center gap-4 text-xs whitespace-nowrap">
            <div className="shrink-0 uppercase tracking-widest text-[hsl(var(--accent))] font-semibold">📢 Announcements</div>
            <div className="flex gap-10 animate-ticker">
              <span>Solar Rooftop Phase-IV opens 15 Jan 2026 · Enhanced subsidy of up to ₹78,000</span>
              <span>Tender PEDA/TND/2026/0105 closes 15 Feb — 50 MW Utility Solar EPC</span>
              <span>CBG empanelment window extended till 28 Feb 2026</span>
              <span>Net Metering process now 100% online via PEDA Digital Portal</span>
              <span>PEDA AI Assistant is live — try the chat icon on your screen</span>
            </div>
          </div>
        </div>
      </section>

      {/* KPI STRIP */}
      <section className="container-x -mt-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPIS.map((k) => (
            <div key={k.label} className="kpi-card bg-card" data-testid="kpi-card">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--primary))]/10 grid place-items-center text-[hsl(var(--primary))]">
                  <k.icon className="h-5 w-5"/>
                </div>
                <div className="badge-status-open">
                  <TrendingUp className="h-3 w-3"/>{k.delta}
                </div>
              </div>
              <div className="mt-4 font-heading text-2xl font-bold">{k.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-tiny text-[hsl(var(--primary))]">Citizen & Industry Services</div>
            <h2 className="mt-1 text-3xl lg:text-4xl font-heading font-bold">Apply. Track. Get certified — all online.</h2>
          </div>
          <Link to="/services" className="hidden md:inline-flex btn-outline">All Services <ArrowRight className="h-4 w-4"/></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s) => (
            <Link key={s.key} to={`/services/${s.key}`} className="group rounded-xl border border-border bg-card p-5 transition-transform duration-200 ease-out hover:-translate-y-1 hover:shadow-[0_12px_36px_rgba(0,0,0,0.10)]" data-testid={`service-${s.key}`}>
              <div className={`h-11 w-11 rounded-lg bg-gradient-to-br ${s.tone} grid place-items-center text-white shadow-md`}>
                <s.icon className="h-5 w-5"/>
              </div>
              <div className="mt-4 font-heading font-semibold text-lg">{s.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.desc}</div>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity">
                Apply now <ArrowRight className="h-4 w-4"/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* IMPACT SECTION */}
      <section className="container-x mt-24 grid lg:grid-cols-12 gap-8 items-stretch">
        <div className="lg:col-span-7 rounded-2xl overflow-hidden relative min-h-[380px]">
          <img src={IMG.engineerSite} alt="Engineers" className="absolute inset-0 h-full w-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-tr from-peda-navyDeep/90 via-peda-navy/60 to-transparent"/>
          <div className="relative p-8 lg:p-12 h-full flex flex-col justify-end text-white">
            <div className="label-tiny text-[hsl(var(--accent))]">Works Management</div>
            <h3 className="mt-2 font-heading font-bold text-3xl">Every project. Every district. Every milestone — visible.</h3>
            <p className="mt-3 max-w-lg text-white/80">From tender floating to bill verification and geo-tagged inspections, our Works platform gives leaders a live pulse on Punjab's clean energy build-out.</p>
            <Link to="/projects" className="btn-primary mt-6 self-start">Explore Projects <ArrowRight className="h-4 w-4"/></Link>
          </div>
        </div>
        <div className="lg:col-span-5 grid grid-rows-2 gap-4">
          <div className="rounded-2xl border border-border p-6 bg-card">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]"/>
              <div className="label-tiny">Governance</div>
            </div>
            <div className="font-heading font-bold text-xl">Transparent, WCAG-compliant, secure</div>
            <p className="text-sm text-muted-foreground mt-2">JWT authentication, role-based access, audit logs, and daily backups. Built to Government of Punjab standards.</p>
          </div>
          <div className="rounded-2xl border border-border p-6 bg-card">
            <div className="flex items-center gap-2 mb-1">
              <Award className="h-4 w-4 text-[hsl(var(--accent))]"/>
              <div className="label-tiny">Recognition</div>
            </div>
            <div className="font-heading font-bold text-xl">National Solar Award 2024</div>
            <p className="text-sm text-muted-foreground mt-2">PEDA recognized by MNRE for outstanding contribution to renewable rollout in Northern India.</p>
          </div>
        </div>
      </section>

      {/* NEWS + GALLERY */}
      <section className="container-x mt-24 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <div className="label-tiny text-[hsl(var(--primary))]">Newsroom</div>
          <h2 className="mt-1 text-3xl lg:text-4xl font-heading font-bold">Latest from PEDA</h2>
          <div className="mt-6 space-y-3">
            {NEWS.map((n, i) => (
              <div key={i} className="rounded-xl border border-border p-5 flex items-start gap-4 hover:border-[hsl(var(--primary))] transition-colors">
                <div className="h-12 w-12 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] grid place-items-center font-heading font-bold">{i+1}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="badge-status-open">{n.tag}</span>
                    <span>{n.date}</span>
                  </div>
                  <div className="mt-1 font-heading font-semibold">{n.title}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground"/>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="label-tiny text-[hsl(var(--accent))]">Gallery</div>
          <h2 className="mt-1 text-3xl lg:text-4xl font-heading font-bold">Punjab, in energy motion</h2>
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[IMG.wheatField, IMG.solarCard, IMG.windSunset, IMG.evCharger, IMG.farmers, IMG.inspector].map((src, i) => (
              <div key={i} className={`rounded-xl overflow-hidden relative ${i === 0 ? "col-span-2 row-span-2 h-64" : "h-32"}`}>
                <img src={src} alt="gallery" className="h-full w-full object-cover"/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x mt-24">
        <div className="relative overflow-hidden rounded-3xl bg-peda-navy text-white p-8 lg:p-14 grid lg:grid-cols-2 gap-8 items-center">
          <div className="absolute inset-0 grain pointer-events-none opacity-60"/>
          <div className="relative">
            <div className="label-tiny text-[hsl(var(--accent))]">Ready to go solar?</div>
            <h3 className="mt-2 font-heading font-bold text-3xl lg:text-4xl">Apply in minutes. Get subsidy in weeks.</h3>
            <p className="mt-3 max-w-lg text-white/80">Register once and access every PEDA scheme — Solar Rooftop, EV, Biogas, Net Metering. Track your application status in real-time.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register" className="btn-primary">Register as Citizen</Link>
              <Link to="/track" className="btn-outline bg-white/5 text-white border-white/20 hover:bg-white/10">Track existing application</Link>
            </div>
          </div>
          <div className="relative grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <div className="label-tiny text-white/60">Step 1</div>
              <div className="mt-1 font-heading font-semibold">Register & KYC</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <div className="label-tiny text-white/60">Step 2</div>
              <div className="mt-1 font-heading font-semibold">Choose Scheme</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <div className="label-tiny text-white/60">Step 3</div>
              <div className="mt-1 font-heading font-semibold">Site Verification</div>
            </div>
            <div className="rounded-xl bg-[hsl(var(--primary))]/20 border border-[hsl(var(--primary))]/40 p-4">
              <div className="label-tiny text-[hsl(var(--accent))]">Step 4</div>
              <div className="mt-1 font-heading font-semibold">Get Subsidy</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
